/**
 * 🔍 Token Debug Utility
 * Helps diagnose JWT token issues in the browser console
 */

export const tokenDebug = {
  /**
   * Decode and display the current JWT token
   */
  inspect() {
    const token = localStorage.getItem('token');

    if (!token) {
      console.error('❌ No token found in localStorage');
      return null;
    }

    try {
      // Decode JWT (format: header.payload.signature)
      const parts = token.split('.');
      if (parts.length !== 3) {
        console.error('❌ Invalid JWT format');
        return null;
      }

      const header = JSON.parse(atob(parts[0]));
      const payload = JSON.parse(atob(parts[1]));

      console.log('🔐 JWT Token Debug Info');
      console.log('━'.repeat(60));

      console.log('\n📋 Header:', header);

      console.log('\n📦 Payload:', payload);

      console.log('\n🔍 Payload Analysis:');
      console.log('  userId:', payload.userId || '❌ MISSING (THIS IS THE PROBLEM!)');
      console.log('  email:', payload.email || '❌ Missing');
      console.log('  role:', payload.role || '❌ Missing');
      console.log('  iat (issued at):', payload.iat ? new Date(payload.iat * 1000).toISOString() : '❌ Missing');
      console.log('  exp (expires at):', payload.exp ? new Date(payload.exp * 1000).toISOString() : '❌ Missing');

      // Check if token is expired
      const now = Math.floor(Date.now() / 1000);
      const isExpired = payload.exp && payload.exp < now;
      console.log('  isExpired:', isExpired ? '⚠️ YES' : '✅ No');

      // Time until expiration
      if (payload.exp && !isExpired) {
        const secondsUntilExpiry = payload.exp - now;
        const hoursUntilExpiry = Math.floor(secondsUntilExpiry / 3600);
        const daysUntilExpiry = Math.floor(hoursUntilExpiry / 24);
        console.log(`  expiresIn: ${daysUntilExpiry}d ${hoursUntilExpiry % 24}h`);
      }

      // Critical issue check
      console.log('\n⚠️ Critical Issues:');
      if (!payload.userId) {
        console.error('  ❌ MISSING userId - Admin endpoints will fail with 401');
        console.error('  ℹ️ Backend must include userId when generating JWT token');
      } else {
        console.log('  ✅ All required fields present');
      }

      console.log('\n━'.repeat(60));

      return { header, payload };
    } catch (error) {
      console.error('❌ Failed to decode token:', error);
      return null;
    }
  },

  /**
   * Compare expected vs actual token structure
   */
  compare() {
    const result = this.inspect();
    if (!result) return;

    const { payload } = result;

    console.log('\n📊 Token Structure Comparison');
    console.log('━'.repeat(60));
    console.log('\nExpected Structure:');
    console.log({
      userId: 'uuid-string (REQUIRED)',
      email: 'user@example.com',
      role: 'super_admin | admin | user',
      iat: 1234567890,
      exp: 1234567890
    });

    console.log('\nActual Structure:');
    console.log(payload);

    console.log('\nField Status:');
    console.log('  userId:', payload.userId ? '✅ Present' : '❌ MISSING - THIS WILL BREAK ADMIN ENDPOINTS');
    console.log('  email:', payload.email ? '✅ Present' : '⚠️ Missing');
    console.log('  role:', payload.role ? '✅ Present' : '⚠️ Missing');
    console.log('  iat:', payload.iat ? '✅ Present' : '⚠️ Missing');
    console.log('  exp:', payload.exp ? '✅ Present' : '⚠️ Missing');
    console.log('━'.repeat(60));
  },

  /**
   * Test if the current token will work with admin endpoints
   */
  testAdminEndpoint() {
    const token = localStorage.getItem('token');

    if (!token) {
      console.error('❌ No token found');
      return false;
    }

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));

      console.log('🧪 Admin Endpoint Compatibility Test');
      console.log('━'.repeat(60));

      const hasUserId = !!payload.userId;
      const hasRole = !!payload.role;
      const isNotExpired = payload.exp && payload.exp > Math.floor(Date.now() / 1000);

      console.log('  Has userId:', hasUserId ? '✅ Yes' : '❌ No (CRITICAL)');
      console.log('  Has role:', hasRole ? '✅ Yes' : '⚠️ No');
      console.log('  Not expired:', isNotExpired ? '✅ Yes' : '❌ No');

      const canUseAdminEndpoints = hasUserId && hasRole && isNotExpired;

      console.log('\nResult:', canUseAdminEndpoints ? '✅ Token should work' : '❌ Token will FAIL on admin endpoints');

      if (!hasUserId) {
        console.error('\n⚠️ CRITICAL: Backend must add userId to JWT token');
        console.error('   See TOKEN_DEBUG_INSTRUCTIONS.md for details');
      }

      console.log('━'.repeat(60));

      return canUseAdminEndpoints;
    } catch (error) {
      console.error('❌ Test failed:', error);
      return false;
    }
  },

  /**
   * Clear token and prompt for re-login
   */
  clearAndRelogin() {
    console.log('🗑️ Clearing authentication data...');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('userId');
    console.log('✅ Cleared. Please login again to get a fresh token.');
    console.log('⚠️ Make sure backend has been fixed to include userId in token!');
  },

  /**
   * Show help
   */
  help() {
    console.log('🔍 Token Debug Utility - Available Commands');
    console.log('━'.repeat(60));
    console.log('  tokenDebug.inspect()           - View current token details');
    console.log('  tokenDebug.compare()           - Compare expected vs actual structure');
    console.log('  tokenDebug.testAdminEndpoint() - Test admin endpoint compatibility');
    console.log('  tokenDebug.clearAndRelogin()   - Clear token and re-login');
    console.log('  tokenDebug.help()              - Show this help');
    console.log('━'.repeat(60));
    console.log('\nQuick Start:');
    console.log('  1. Run: tokenDebug.inspect()');
    console.log('  2. Check if userId is present');
    console.log('  3. If missing, contact backend team');
    console.log('  4. After backend fix, run: tokenDebug.clearAndRelogin()');
  }
};

// Expose to window for browser console access
if (typeof window !== 'undefined') {
  (window as any).tokenDebug = tokenDebug;
  console.log('🔍 Token Debug Utility loaded. Type "tokenDebug.help()" for commands.');
}

export default tokenDebug;
